/**
 * Custom event types: the network's own events (a swipe, a video watched to
 * the end, a purchase) that a creative reports to the adserver alongside
 * impressions and clicks. The ID is what the creative sends as `customType`,
 * and an event can also count as a conversion for CPA line items.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { fetchAllPages, sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** How the event's custom value is read — mirrors `CustomEventType.ValueType` in core. */
export const CUSTOM_EVENT_VALUE_TYPES = ['number', 'milliseconds', 'seconds', 'money'] as const
export type CustomEventValueType = (typeof CUSTOM_EVENT_VALUE_TYPES)[number]

/** Whether reports show a sum (with an average) or a plain count — `CustomEventType.Type`. */
export const CUSTOM_EVENT_DISPLAY_TYPES = ['SUM', 'COUNT'] as const
export type CustomEventDisplayType = (typeof CUSTOM_EVENT_DISPLAY_TYPES)[number]

/** What the event is a conversion of, and within which window — `CustomEventType.Conversion`. */
export const CUSTOM_EVENT_CONVERSION_TYPES = [
  'IMPRESSION',
  'IMPRESSION_ONE_DAY',
  'IMPRESSION_ONE_WEEK',
  'IMPRESSION_TWO_WEEKS',
  'IMPRESSION_THIRTY_DAYS',
  'CLICK',
  'CLICK_ONE_DAY',
  'CLICK_ONE_WEEK',
  'CLICK_TWO_WEEKS',
  'CLICK_THIRTY_DAYS',
] as const
export type CustomEventConversionType = (typeof CUSTOM_EVENT_CONVERSION_TYPES)[number]

export interface CustomEventType {
  id: string
  name: string
  description: string
  valueType: CustomEventValueType | ''
  type: CustomEventDisplayType
  conversionType: CustomEventConversionType | null
  priority: number
  objectState: string
  createTime: string
  updateTime: string
  /** Problems the server found on the last save; shown on the edit page. */
  validationWarnings: ValidationWarning[]
}

export interface CustomEventTypeInput {
  name: string
  description?: string
  valueType?: CustomEventValueType
  type?: CustomEventDisplayType
  /** `null` clears it — the event then no longer tracks conversions. */
  conversionType?: CustomEventConversionType | null
  priority?: number
  objectState?: string
}

function mapCustomEventType(r: Record<string, unknown>): CustomEventType {
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: (r.description as string) ?? '',
    valueType: (r.valueType as CustomEventValueType) ?? '',
    type: (r.type as CustomEventDisplayType) ?? 'COUNT',
    conversionType: (r.conversionType as CustomEventConversionType) ?? null,
    priority: (r.priority as number) ?? 0,
    objectState: (r.objectState as string) ?? '',
    createTime: (r.createTime as string) ?? '',
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    updateTime: (r.updateTime as string) ?? '',
  }
}

/** The snippets a layout runs to report the event, as UII shows them. */
export function customEventExampleCodes(eventId: string) {
  return {
    simple: `adn.inIframe.sendCustomEvent('{{adId}}', {customType: '${eventId}'});`,
    withValue: `adn.inIframe.sendCustomEvent('{{adId}}', {customType: '${eventId}', customValue: 2});`,
    multiple: `adn.inIframe.sendCustomEvent('{{adId}}', {events:[{customType: '${eventId}', customValue: 2}, {customType: 'another_custom_event_id'}]});`,
  }
}

export interface CustomEventTypeListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

/** One page of custom events, narrowed by the given filters on the server. */
async function fetchCustomEventTypePage(
  { search, status, sort }: CustomEventTypeListOptions,
  page: number,
  pageSize: number
): Promise<ListPage<CustomEventType>> {
  const params = new URLSearchParams({
    ...searchParams('customEventTypes', search),
    context: getContext(),
    includeHidden: 'false',
    ...statusParams(status),
    ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
    page: String(page),
    pageSize: String(pageSize),
  })
  const res = await apiFetch(`${API_BASE}/customeventtypes?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch custom events')
  const data = (await res.json()) as { totalCount?: number }
  const results = toArray<Record<string, unknown>>(data).map(mapCustomEventType)
  return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
}

export const CustomEventTypesApi = {
  getPage: ({ page = 1, pageSize = 25, ...filters }: CustomEventTypeListOptions = {}) =>
    fetchCustomEventTypePage(filters, page, pageSize),

  /** Every custom event matching the search, walked page by page — for pickers. */
  getAll: (search?: string): Promise<CustomEventType[]> =>
    fetchAllPages((page, pageSize) => fetchCustomEventTypePage({ search }, page, pageSize)),

  getById: async (id: string): Promise<CustomEventType> => {
    const res = await apiFetch(`${API_BASE}/customeventtypes/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch custom event')
    return mapCustomEventType(await res.json())
  },

  create: async (id: string, input: CustomEventTypeInput): Promise<CustomEventType> => {
    const res = await apiFetch(`${API_BASE}/customeventtypes/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...input }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create custom event')
    return mapCustomEventType(await res.json())
  },

  update: async (id: string, input: CustomEventTypeInput): Promise<CustomEventType> => {
    const res = await apiFetch(`${API_BASE}/customeventtypes/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save custom event')
    return mapCustomEventType(await res.json())
  },
}
